/**
 * Text processing utilities for Korean grammar checking
 * Handles text normalization, chunking, and position tracking
 */

import type { TextSpan } from '../types/grammar';

/**
 * Default configuration for text processing
 */
export const TEXT_PROCESSING_CONFIG = {
  /** Maximum characters per chunk for API processing */
  MAX_CHUNK_SIZE: 4500,
  /** Overlap size between chunks to handle boundary errors */
  CHUNK_OVERLAP: 100,
  /** Maximum text length for single processing */
  MAX_TEXT_LENGTH: 50000,
  /** Minimum text length to trigger chunking */
  MIN_CHUNK_LENGTH: 1000
};

/**
 * Smart punctuation replacement mappings
 */
const SMART_PUNCTUATION_MAP: Record<string, string> = {
  // Smart quotes
  '\u201C': '"',  // Left double quotation mark
  '\u201D': '"',  // Right double quotation mark
  '\u2018': "'",  // Left single quotation mark
  '\u2019': "'",  // Right single quotation mark
  '\u201A': "'",  // Single low-9 quotation mark
  '\u201E': '"',  // Double low-9 quotation mark
  
  // Dashes
  '\u2014': '-',  // Em dash
  '\u2013': '-',  // En dash
  '\u2015': '-',  // Horizontal bar
  
  // Ellipsis
  '\u2026': '...',  // Horizontal ellipsis
  '\u22EF': '...',  // Midline horizontal ellipsis
  
  // Spaces
  '\u00A0': ' ',  // Non-breaking space
  '\u2002': ' ',  // En space
  '\u2003': ' ',  // Em space
  '\u2004': ' ',  // Three-per-em space
  '\u2005': ' ',  // Four-per-em space
  '\u2006': ' ',  // Six-per-em space
  '\u3000': ' ',  // Ideographic space
  
  // Other punctuation
  '\u00B7': '\u00B7',  // Middle dot (keep as is for Korean)
  '\u203B': '*',  // Reference mark
  '\u300F': ']',  // Right white corner bracket
  '\u300E': '[',  // Left white corner bracket
  '\u300D': '"',  // Right corner bracket
  '\u300C': '"',  // Left corner bracket
};

/**
 * Clean smart punctuation and normalize text
 * @param text - Text to clean
 * @returns Cleaned text with normalized punctuation
 */
export function cleanSmartPunctuation(text: string): string {
  if (!text) return '';
  
  let cleaned = text;
  
  // Replace smart punctuation
  for (const [smart, normal] of Object.entries(SMART_PUNCTUATION_MAP)) {
    cleaned = cleaned.replace(new RegExp(smart, 'g'), normal);
  }
  
  // Normalize multiple spaces to single space
  cleaned = cleaned.replace(/\s+/g, ' ');
  
  // Normalize line breaks
  cleaned = cleaned.replace(/\r\n/g, '\n');
  cleaned = cleaned.replace(/\r/g, '\n');
  
  // Remove zero-width characters
  cleaned = cleaned.replace(/[\u200B\u200C\u200D\uFEFF]/g, '');
  
  return cleaned;
}

/**
 * Text chunk with position information
 */
export interface TextChunk {
  /** Chunk text content */
  text: string;
  /** Start position in original text */
  startOffset: number;
  /** End position in original text */
  endOffset: number;
  /** Chunk index */
  index: number;
  /** Whether this is the last chunk */
  isLast: boolean;
}

/**
 * Split text into chunks for batch processing
 * @param text - Text to split
 * @param maxChunkSize - Maximum size of each chunk
 * @param overlap - Overlap size between chunks
 * @returns Array of text chunks
 */
export function splitIntoChunks(
  text: string,
  maxChunkSize: number = TEXT_PROCESSING_CONFIG.MAX_CHUNK_SIZE,
  overlap: number = TEXT_PROCESSING_CONFIG.CHUNK_OVERLAP
): TextChunk[] {
  if (!text || text.length <= maxChunkSize) {
    return [{
      text,
      startOffset: 0,
      endOffset: text.length,
      index: 0,
      isLast: true
    }];
  }
  
  const chunks: TextChunk[] = [];
  let currentOffset = 0;
  let chunkIndex = 0;
  
  while (currentOffset < text.length) {
    // Calculate chunk end position
    let endOffset = Math.min(currentOffset + maxChunkSize, text.length);
    
    // If not the last chunk, try to break at a sentence or word boundary
    if (endOffset < text.length) {
      // Try to find a sentence boundary (. ! ?)
      const sentenceBoundary = findLastSentenceBoundary(
        text,
        currentOffset,
        endOffset
      );
      
      if (sentenceBoundary > currentOffset) {
        endOffset = sentenceBoundary;
      } else {
        // Try to find a word boundary
        const wordBoundary = findLastWordBoundary(
          text,
          currentOffset,
          endOffset
        );
        
        if (wordBoundary > currentOffset) {
          endOffset = wordBoundary;
        }
      }
    }
    
    // Create chunk
    chunks.push({
      text: text.substring(currentOffset, endOffset),
      startOffset: currentOffset,
      endOffset,
      index: chunkIndex++,
      isLast: endOffset >= text.length
    });
    
    // Move to next chunk with overlap
    currentOffset = endOffset - overlap;
    
    // Ensure we make progress
    if (currentOffset <= chunks[chunks.length - 1].startOffset) {
      currentOffset = endOffset;
    }
  }
  
  return chunks;
}

/**
 * Find the last sentence boundary in a text range
 * @param text - Full text
 * @param start - Start position
 * @param end - End position
 * @returns Position of last sentence boundary, or -1 if not found
 */
function findLastSentenceBoundary(
  text: string,
  start: number,
  end: number
): number {
  const sentenceEnders = ['.', '!', '?', '。', '！', '？'];
  let lastBoundary = -1;
  
  for (let i = end - 1; i > start; i--) {
    if (sentenceEnders.includes(text[i])) {
      // Check if followed by space or newline (or end of text)
      if (i + 1 >= text.length || /\s/.test(text[i + 1])) {
        lastBoundary = i + 1;
        break;
      }
    }
  }
  
  return lastBoundary;
}

/**
 * Find the last word boundary in a text range
 * @param text - Full text
 * @param start - Start position
 * @param end - End position
 * @returns Position of last word boundary, or -1 if not found
 */
function findLastWordBoundary(
  text: string,
  start: number,
  end: number
): number {
  let lastBoundary = -1;
  
  for (let i = end - 1; i > start; i--) {
    // Check for space or newline
    if (/\s/.test(text[i])) {
      lastBoundary = i;
      break;
    }
    // Check for Korean particle boundary
    if (i > 0 && isKoreanParticleBoundary(text[i - 1], text[i])) {
      lastBoundary = i;
      break;
    }
  }
  
  return lastBoundary;
}

/**
 * Check if there's a Korean particle boundary between two characters
 * @param prev - Previous character
 * @param curr - Current character
 * @returns True if this is a particle boundary
 */
function isKoreanParticleBoundary(prev: string, curr: string): boolean {
  // Common Korean particles
  const particles = ['은', '는', '이', '가', '을', '를', '에', '에서', '으로', '로', '와', '과', '의', '도'];
  
  // Check if current character could start a particle
  for (const particle of particles) {
    if (curr === particle[0]) {
      return true;
    }
  }
  
  return false;
}

/**
 * Calculate the actual position of text in the original content
 * accounting for any transformations
 * @param originalText - Original text before processing
 * @param processedText - Text after processing
 * @param processedPosition - Position in processed text
 * @returns Position in original text
 */
export function mapProcessedToOriginalPosition(
  originalText: string,
  processedText: string,
  processedPosition: number
): number {
  // If texts are identical, return the same position
  if (originalText === processedText) {
    return processedPosition;
  }
  
  // Build a mapping between processed and original positions
  let originalIndex = 0;
  let processedIndex = 0;
  
  while (processedIndex < processedPosition && originalIndex < originalText.length) {
    const originalChar = originalText[originalIndex];
    const processedChar = processedText[processedIndex];
    
    if (processedChar === originalChar) {
      // Characters match, advance both
      originalIndex++;
      processedIndex++;
    } else if (SMART_PUNCTUATION_MAP[originalChar]) {
      // Original has smart punctuation that was replaced
      const replacement = SMART_PUNCTUATION_MAP[originalChar];
      if (processedText.substr(processedIndex, replacement.length) === replacement) {
        originalIndex++;
        processedIndex += replacement.length;
      } else {
        // Mismatch, try to resync
        originalIndex++;
      }
    } else if (/\s/.test(originalChar) && /\s/.test(processedChar)) {
      // Both are whitespace, advance both
      originalIndex++;
      processedIndex++;
    } else if (/\s/.test(originalChar)) {
      // Original has whitespace that was removed
      originalIndex++;
    } else {
      // Unknown transformation, advance both
      originalIndex++;
      processedIndex++;
    }
  }
  
  return Math.min(originalIndex, originalText.length);
}

/**
 * Merge overlapping text spans
 * @param spans - Array of text spans
 * @returns Merged text spans
 */
export function mergeOverlappingSpans(spans: TextSpan[]): TextSpan[] {
  if (spans.length <= 1) return spans;
  
  // Sort by begin offset
  const sorted = [...spans].sort((a, b) => a.beginOffset - b.beginOffset);
  
  const merged: TextSpan[] = [];
  let current = sorted[0];
  
  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];
    const currentEnd = current.beginOffset + current.length;
    
    if (next.beginOffset <= currentEnd) {
      // Spans overlap, merge them
      const newEnd = Math.max(currentEnd, next.beginOffset + next.length);
      current = {
        content: current.content + next.content.substring(
          Math.max(0, currentEnd - next.beginOffset)
        ),
        beginOffset: current.beginOffset,
        length: newEnd - current.beginOffset
      };
    } else {
      // No overlap, add current and move to next
      merged.push(current);
      current = next;
    }
  }
  
  merged.push(current);
  return merged;
}

/**
 * Extract context around a position in text
 * @param text - Full text
 * @param position - Position to extract context around
 * @param contextSize - Size of context to extract (default 50 chars each side)
 * @returns Context string with position marked
 */
export function extractContext(
  text: string,
  position: number,
  contextSize: number = 50
): string {
  const start = Math.max(0, position - contextSize);
  const end = Math.min(text.length, position + contextSize);
  
  let context = '';
  
  if (start > 0) {
    context += '...';
  }
  
  context += text.substring(start, position);
  context += '【' + text.substring(position, Math.min(position + 20, end)) + '】';
  context += text.substring(Math.min(position + 20, end), end);
  
  if (end < text.length) {
    context += '...';
  }
  
  return context;
}

/**
 * Count Korean characters in text
 * @param text - Text to analyze
 * @returns Number of Korean characters
 */
export function countKoreanCharacters(text: string): number {
  if (!text) return 0;
  
  // Korean Unicode ranges:
  // Hangul Syllables: 0xAC00-0xD7AF
  // Hangul Jamo: 0x1100-0x11FF
  // Hangul Compatibility Jamo: 0x3130-0x318F
  const koreanRegex = /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]/g;
  const matches = text.match(koreanRegex);
  
  return matches ? matches.length : 0;
}

/**
 * Check if text is primarily Korean
 * @param text - Text to check
 * @param threshold - Percentage threshold (0-1)
 * @returns True if text is primarily Korean
 */
export function isPrimarilyKorean(text: string, threshold: number = 0.3): boolean {
  if (!text) return false;
  
  const totalChars = text.replace(/\s/g, '').length;
  if (totalChars === 0) return false;
  
  const koreanChars = countKoreanCharacters(text);
  return (koreanChars / totalChars) >= threshold;
}

/**
 * Normalize Korean text for comparison
 * @param text - Text to normalize
 * @returns Normalized text
 */
export function normalizeKoreanText(text: string): string {
  if (!text) return '';
  
  let normalized = text;
  
  // Normalize Hangul compatibility Jamo to modern Jamo
  const compatibilityMap: Record<string, string> = {
    'ㆍ': '·',  // Middle dot
    'ㆎ': 'ㅏ',  // Simplified vowels
  };
  
  for (const [old, replacement] of Object.entries(compatibilityMap)) {
    normalized = normalized.replace(new RegExp(old, 'g'), replacement);
  }
  
  // Remove spaces between Korean characters (for comparison)
  normalized = normalized.replace(/([\uAC00-\uD7AF])\s+([\uAC00-\uD7AF])/g, '$1$2');
  
  return normalized;
}